import React, { useState, useEffect, useRef } from 'react';
import Swal from 'sweetalert2';
import $ from 'jquery';
import 'select2/dist/css/select2.min.css';
import 'select2';

const ReferralSettings = ({ businessId, token, branches = [] }) => {
    const [loading, setLoading] = useState(false);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [settings, setSettings] = useState({
        referral: '0',
        loyalty_points_per_referral: 100,
        promo_code_discount_percentage: 10
    });
    const branchSelectRef = useRef(null);
    const isSelect2Initialized = useRef(false);

    const fetchBranchSettings = async (branchId) => {
        if (!branchId) return;
        
        try {
            const response = await fetch(
                `${process.env.REACT_APP_BASE_URL}/api/branch-referral-settings/${branchId}?business_id=${businessId}`,
                {
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            const data = await response.json();
            
            if (data.success && data.settings) {
                setSettings({
                    referral: data.settings.referral || '0',
                    loyalty_points_per_referral: data.settings.loyalty_points_per_referral || 100,
                    promo_code_discount_percentage: data.settings.promo_code_discount_percentage || 10
                });
            } else {
                setSettings({
                    referral: '0',
                    loyalty_points_per_referral: 100,
                    promo_code_discount_percentage: 10
                });
            }
        } catch (error) {
            console.error('Error fetching branch settings:', error);
        }
    };

    useEffect(() => {
        if (selectedBranchId) {
            fetchBranchSettings(selectedBranchId);
        }
    }, [selectedBranchId]);

    useEffect(() => {
        if (branchSelectRef.current && branches && branches.length > 0 && !isSelect2Initialized.current) {
            if ($(branchSelectRef.current).data('select2')) {
                $(branchSelectRef.current).select2('destroy');
            }
            
            $(branchSelectRef.current).select2({
                placeholder: '-- Select Branch --',
                allowClear: true,
                width: '100%'
            });
            
            $(branchSelectRef.current).on('change', function(e) {
                setSelectedBranchId(e.target.value);
            });
            
            isSelect2Initialized.current = true;
        }
        
        return () => {
            if (branchSelectRef.current && isSelect2Initialized.current) {
                try {
                    if ($(branchSelectRef.current).data('select2')) {
                        $(branchSelectRef.current).select2('destroy');
                    }
                } catch (err) {
                    console.warn('Error destroying select2:', err);
                }
                isSelect2Initialized.current = false;
            }
        };
    }, [branches]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!selectedBranchId) {
            Swal.fire({ icon: 'warning', title: 'Warning', text: 'Please select a branch' });
            return;
        }
        
        const settingsData = {
            branch_id: parseInt(selectedBranchId),
            business_id: businessId,
            referral: settings.referral,
            loyalty_points_per_referral: settings.loyalty_points_per_referral,
            promo_code_discount_percentage: settings.promo_code_discount_percentage
        };
        
        setLoading(true);
        try {
            const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/branch-referral-settings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify(settingsData)
            });
            
            const result = await response.json();
            if (result.success) {
                Swal.fire({ icon: 'success', title: 'Success', text: 'Referral settings saved successfully!' });
            } else {
                Swal.fire({ icon: 'error', title: 'Error', text: result.error || 'Failed to save settings' });
            }
        } catch (error) {
            console.error('Error saving settings:', error);
            Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to save settings' });
        } finally {
            setLoading(false);
        }
    };

    const isReferralEnabled = settings.referral === '1';

    return (
        <div className="content">
            <div className="col-sm-12 print-hidden">
                <div className="card-box">
                    <form onSubmit={handleSubmit}>
                        <div className="row">
                            <div className="col-lg-4 col-md-6 col-sm-12 form-group">
                                <label className="control-label">Select Branch <span className="text-danger">*</span></label>
                                <select
                                    ref={branchSelectRef}
                                    className="form-control"
                                    id="refSettingbranch_id"
                                    required
                                >
                                    <option value="">-- Select Branch --</option>
                                    {branches && branches.map((branch) => (
                                        <option key={branch.id_branch} value={branch.id_branch}>
                                            {branch.branch_name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            
                            <div className="col-lg-4 col-md-6 col-sm-12 form-group">
                                <label className="control-label">Referral Module</label>
                                <select 
                                    className="form-control" 
                                    value={settings.referral}
                                    onChange={(e) => setSettings({...settings, referral: e.target.value})}
                                >
                                    <option value="1">Enabled</option>
                                    <option value="0">Disabled</option>
                                </select>
                                <small className="text-muted">Enable or disable the referral system for this branch</small>
                            </div>

                            <div className="col-lg-4 col-md-6 col-sm-12 form-group">
                                <label className="control-label">Loyalty Points Per Referral</label>
                                <input
                                    className="form-control"
                                    type="number"
                                    value={settings.loyalty_points_per_referral}
                                    onChange={(e) => setSettings({...settings, loyalty_points_per_referral: parseInt(e.target.value) || 0})}
                                    min="0"
                                    disabled={!isReferralEnabled}
                                />
                                <small className="text-muted">Points awarded when referral promo is used</small>
                            </div>

                            <div className="col-lg-4 col-md-6 col-sm-12 form-group">
                                <label className="control-label">Promo Code Discount %</label>
                                <input
                                    className="form-control"
                                    type="number"
                                    value={settings.promo_code_discount_percentage}
                                    onChange={(e) => setSettings({...settings, promo_code_discount_percentage: parseInt(e.target.value) || 0})}
                                    min="0"
                                    max="100"
                                    step="1"
                                    disabled={!isReferralEnabled}
                                />
                                <small className="text-muted">Discount percentage for referral promo codes</small>
                            </div>
                        </div>
                        <div className="row mt-4">
                            <div className="col-12">
                                <div className="alert alert-info">
                                    <i className="fa fa-info-circle"></i> <strong>Note:</strong> SMS Conditions are business-level settings and can be configured in Business Settings.
                                </div>
                            </div>
                        </div>

                        <div className="row mt-3">
                            <div className="col-12">
                                <button className="btn btn-primary" type="submit" disabled={loading}>
                                    <i className="fa fa-save"></i> {loading ? 'Saving...' : 'Save Referral Settings'}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ReferralSettings;